import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { todayString, parseApiDateOnly, toApiDateOnly, formatDate } from '../utils/appDateTime';
import { openFeeReceiptPortal } from '../utils/documentPortalUrl';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import DateInput from './ui/date-input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  ArrowLeft,
  Receipt,
  Save,
  Users,
  Mail,
  Phone,
  GraduationCap,
  ChevronDown,
  ChevronUp,
  Loader2,
  IndianRupee,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { dispatchFeeDataChanged } from '../utils/feeDataEvents';

/** Fee/payment section icon — falls back to ₹ if lucide export missing. */
function RecordPaymentIcon({ className }) {
  if (typeof IndianRupee === 'function') {
    return <IndianRupee className={className} aria-hidden />;
  }
  return (
    <span className={className} aria-hidden>
      ₹
    </span>
  );
}

const ParentAccountDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canManageFeature } = useAuth();
  const canManageFees = canManageFeature('fees');
  const { toast } = useToast();

  const [account, setAccount] = useState(null);
  const [summary, setSummary] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedChild, setExpandedChild] = useState(null);
  const [expandedPayment, setExpandedPayment] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [openingReceiptId, setOpeningReceiptId] = useState(null);
  const defaultNextDueDate = (paymentDate) => {
    const base = paymentDate || todayString();
    const d = parseApiDateOnly(base);
    if (!d) return '';
    d.setMonth(d.getMonth() + 1);
    return toApiDateOnly(d);
  };

  const [payForm, setPayForm] = useState({
    payment_date: todayString(),
    payment_method: 'cash',
    remarks: '',
    next_due_date: defaultNextDueDate(),
    allocations: {},
  });

  const getSelectedAcademicYearId = () => {
    const schoolId = user?.school_id;
    if (!schoolId) return null;
    return localStorage.getItem(`selected_academic_year_id_${schoolId}`);
  };

  const formatCurrency = (n) => {
    const num = Number(n);
    if (Number.isNaN(num)) return '—';
    return `₹${num.toLocaleString('en-IN')}`;
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const academicYearId = getSelectedAcademicYearId();
      const params = academicYearId ? { academic_year_id: academicYearId } : {};
      const [detailRes, summaryRes, paymentsRes] = await Promise.all([
        api.get(`/fees/parent-accounts/${id}`, { params }),
        api.get(`/fees/parent-accounts/${id}/summary`, { params }),
        api.get(`/fees/parent-accounts/${id}/payments`),
      ]);
      const acc = detailRes.data.data?.account || null;
      setAccount(acc);
      setSummary(summaryRes.data.data || null);
      setPayments(paymentsRes.data.data || []);

      const alloc = {};
      (summaryRes.data.data?.children || []).forEach((c) => {
        alloc[c.student_id] = '';
      });
      const paymentDate = todayString();
      const totalPending = Number(summaryRes.data.data?.total_pending ?? 0);
      setPayForm((prev) => ({
        ...prev,
        allocations: alloc,
        payment_date: paymentDate,
        next_due_date: totalPending > 0.01
          ? (acc?.next_due_date || defaultNextDueDate(paymentDate))
          : '',
      }));
    } catch (e) {
      setAccount(null);
      setSummary(null);
      setPayments([]);
      const status = e.response?.status;
      const apiMessage = e.response?.data?.message;
      if (status === 403) {
        setError('You do not have permission to view this parent account.');
      } else if (status === 404) {
        setError(null);
      } else {
        setError(apiMessage || 'Failed to load parent account.');
      }
    } finally {
      setLoading(false);
    }
  }, [id, user?.school_id]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const handler = () => loadAll();
    window.addEventListener('academicYearChanged', handler);
    return () => window.removeEventListener('academicYearChanged', handler);
  }, [loadAll]);

  const handleAllocationChange = (studentId, value) => {
    setPayForm((prev) => ({
      ...prev,
      allocations: { ...prev.allocations, [studentId]: value },
    }));
  };

  const willRemainAfterPayment = useMemo(() => {
    const totalPending = Number(summary?.total_pending ?? 0);
    let paymentTotal = 0;
    for (const [, raw] of Object.entries(payForm.allocations)) {
      const amount = parseFloat(raw);
      if (amount > 0) paymentTotal += amount;
    }
    return Math.max(0, totalPending - paymentTotal);
  }, [summary?.total_pending, payForm.allocations]);

  const isFullyPaid = Number(summary?.total_pending ?? 0) <= 0.01;

  const validateCollectForm = () => {
    if (!payForm.payment_date) {
      return 'Payment date is required';
    }
    const children = summary?.children || [];
    let paymentTotal = 0;
    for (const [, raw] of Object.entries(payForm.allocations)) {
      const amount = parseFloat(raw);
      if (amount > 0) paymentTotal += amount;
    }
    const totalPending = Number(summary?.total_pending ?? 0);
    const willRemain = Math.max(0, totalPending - paymentTotal);
    if (willRemain > 0.01) {
      if (!payForm.next_due_date) {
        return 'Next due date is required when a balance remains after payment';
      }
      if (payForm.next_due_date < payForm.payment_date) {
        return 'Next due date must be on or after payment date';
      }
    } else if (payForm.next_due_date && payForm.next_due_date < payForm.payment_date) {
      return 'Next due date must be on or after payment date';
    }
    for (const [studentId, raw] of Object.entries(payForm.allocations)) {
      const amount = parseFloat(raw);
      if (!amount || amount <= 0) continue;
      const child = children.find((c) => String(c.student_id) === String(studentId));
      const maxPending = Number(child?.pending ?? 0);
      if (amount > maxPending + 0.01) {
        return `Amount for ${child?.first_name || 'student'} exceeds pending (${formatCurrency(maxPending)})`;
      }
    }
    return null;
  };

  const handleCollect = async (e) => {
    e.preventDefault();
    if (collecting) return;

    const validationError = validateCollectForm();
    if (validationError) {
      toast({ title: 'Validation error', description: validationError, variant: 'destructive' });
      return;
    }

    const allocations = Object.entries(payForm.allocations)
      .map(([student_id, amount]) => ({
        student_id: parseInt(student_id, 10),
        amount: parseFloat(amount),
      }))
      .filter((a) => a.amount > 0);

    if (allocations.length === 0) {
      toast({ title: 'Error', description: 'Enter amount for at least one student', variant: 'destructive' });
      return;
    }

    setCollecting(true);
    try {
      const academicYearId = getSelectedAcademicYearId();
      const payload = {
        payment_date: payForm.payment_date,
        payment_method: payForm.payment_method,
        remarks: payForm.remarks,
        allocations,
        ...(academicYearId ? { academic_year_id: academicYearId } : {}),
      };
      if (willRemainAfterPayment > 0.01) {
        payload.next_due_date = payForm.next_due_date;
      }
      const res = await api.post(`/fees/parent-accounts/${id}/collect`, payload);
      const data = res.data.data || {};
      if (data.account) {
        setAccount((prev) => ({ ...prev, ...data.account }));
      }
      if (data.summary) {
        setSummary(data.summary);
        const alloc = {};
        (data.summary.children || []).forEach((c) => {
          alloc[c.student_id] = '';
        });
        const pendingAfter = Number(data.summary?.total_pending ?? data.remaining_balance ?? 0);
        setPayForm((prev) => ({
          ...prev,
          allocations: alloc,
          next_due_date: pendingAfter > 0.01
            ? (data.account?.next_due_date || data.next_due_date || defaultNextDueDate(prev.payment_date))
            : '',
        }));
      }

      dispatchFeeDataChanged({
        parentAccountId: Number(id),
        schoolId: user?.school_id,
        academicYearId: academicYearId || null,
      });

      toast({
        title: 'Success',
        description: `Payment recorded. Receipt: ${data.receipt_no || ''}`,
      });

      const paymentsRes = await api.get(`/fees/parent-accounts/${id}/payments`);
      const batchList = paymentsRes.data.data || [];
      setPayments(batchList);

      const newBatch = batchList.find((b) => b.id === data.batch_id) || batchList[0];
      const paymentId = data.primary_payment_id || newBatch?.primary_payment_id;
      if (paymentId) {
        try {
          await openFeeReceiptPortal({ id: paymentId }, api);
        } catch {
          // receipt optional; payment already saved
        }
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.message || 'Payment failed',
        variant: 'destructive',
      });
    } finally {
      setCollecting(false);
    }
  };

  const handleOpenReceipt = async (batch) => {
    const paymentId = batch.primary_payment_id;
    if (!paymentId || openingReceiptId) return;
    setOpeningReceiptId(batch.id);
    try {
      await openFeeReceiptPortal({ id: paymentId }, api);
    } catch (err) {
      toast({
        title: 'Could not open receipt',
        description: err?.response?.data?.message || err?.message,
        variant: 'destructive',
      });
    } finally {
      setOpeningReceiptId(null);
    }
  };

  const childCount = summary?.children?.length ?? 0;

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 space-y-4">
        <p className="text-red-500">{error}</p>
        <Link to="/fees/accounts" className="text-blue-500 inline-block">Back to accounts</Link>
      </div>
    );
  }

  if (!account) {
    return (
      <div className="text-center py-12 space-y-4">
        <p className="text-muted-foreground">Parent account not found.</p>
        <Link to="/fees/accounts" className="text-blue-500 inline-block">Back to accounts</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <Link to="/fees/accounts">
          <Button variant="outline" size="sm" className="border-border mt-1">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </Link>
        <Card className="border-border flex-1">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center shrink-0">
                <Users className="w-8 h-8 text-blue-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold text-foreground">{account.name}</h1>
                  <Badge variant="outline" className="border-border text-foreground">
                    {childCount} student{childCount !== 1 ? 's' : ''}
                  </Badge>
                  {isFullyPaid && (
                    <Badge className="bg-green-600 hover:bg-green-600 text-white">Paid</Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Phone className="w-4 h-4 shrink-0" />
                    {account.mobile || '—'}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Mail className="w-4 h-4 shrink-0" />
                    {account.email || '—'}
                  </span>
                  {!isFullyPaid && account.next_due_date && (
                    <span className="text-amber-600 dark:text-amber-400">
                      Next due: {formatDate(account.next_due_date)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Total yearly fees</p>
            <p className="text-2xl font-bold text-foreground mt-1">{formatCurrency(summary?.total_yearly)}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Paid</p>
            <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(summary?.total_paid)}</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Pending</p>
            <p className="text-2xl font-bold text-amber-600 mt-1">{formatCurrency(summary?.total_pending)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <GraduationCap className="w-5 h-5" /> Linked children
          </CardTitle>
          <p className="text-muted-foreground text-sm font-normal">
            All students under this parent account. Fees shown for selected academic year.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          {(summary?.children || []).length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">No linked students yet.</p>
          ) : (
            (summary?.children || []).map((child) => {
              const isExpanded = expandedChild === child.student_id;
              const hasBreakdown = Array.isArray(child.breakdown) && child.breakdown.length > 0;
              return (
                <div
                  key={child.student_id}
                  className="border border-border rounded-lg overflow-hidden cursor-pointer hover:bg-muted/40 transition-colors"
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(`/students/${child.student_id}`)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/students/${child.student_id}`);
                    }
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-muted/30">
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-blue-500">
                        {child.first_name} {child.last_name}
                      </span>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {child.admission_no}
                        {child.class_name ? ` · ${child.class_name}` : ''}
                        {child.section_name ? ` · ${child.section_name}` : ''}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm sm:text-right">
                      <div>
                        <p className="text-xs text-muted-foreground">Yearly</p>
                        <p className="font-medium text-foreground">{formatCurrency(child.yearly_total)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Paid</p>
                        <p className="font-medium text-green-600">{formatCurrency(child.paid)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Pending</p>
                        <p className="font-medium text-amber-600">{formatCurrency(child.pending)}</p>
                      </div>
                    </div>
                    {hasBreakdown && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedChild(isExpanded ? null : child.student_id);
                        }}
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                  {isExpanded && hasBreakdown && (
                    <div
                      className="px-4 pb-4 border-t border-border"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border">
                            <TableHead className="text-muted-foreground">Fee type</TableHead>
                            <TableHead className="text-muted-foreground text-right">Structure</TableHead>
                            <TableHead className="text-muted-foreground text-right">Yearly</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {child.breakdown.map((row) => {
                            const included = row.included !== false;
                            return (
                            <TableRow
                              key={`${child.student_id}-${row.fee_type_id}`}
                              className={`border-border ${included ? '' : 'opacity-50'}`}
                            >
                              <TableCell className="text-foreground">
                                {row.fee_type_name}
                                {!included && (
                                  <span className="ml-2 text-xs text-muted-foreground">(not enabled)</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right text-muted-foreground">
                                {formatCurrency(row.structure_amount)} / {row.frequency}
                              </TableCell>
                              <TableCell className="text-right">
                                {included ? formatCurrency(row.yearly_amount) : '—'}
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      {canManageFees && !isFullyPaid && (summary?.children || []).length > 0 && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <RecordPaymentIcon className="w-5 h-5 inline" /> Record payment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCollect} className="space-y-4">
              {(summary?.children || []).map((child) => (
                <div key={child.student_id} className="flex flex-col sm:flex-row sm:items-end gap-2 border-b border-border pb-3">
                  <div className="flex-1">
                    <Label className="text-foreground">
                      {child.first_name} {child.last_name} (pending {formatCurrency(child.pending)})
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Amount"
                      value={payForm.allocations[child.student_id] ?? ''}
                      onChange={(e) => handleAllocationChange(child.student_id, e.target.value)}
                      className="bg-muted border-border mt-1"
                    />
                  </div>
                </div>
              ))}
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label>Payment date</Label>
                  <DateInput
                    value={payForm.payment_date}
                    onChange={(v) => setPayForm({ ...payForm, payment_date: v })}
                    className="bg-muted border-border"
                  />
                </div>
                {willRemainAfterPayment > 0.01 && (
                  <div>
                    <Label>Next due date</Label>
                    <DateInput
                      value={payForm.next_due_date}
                      onChange={(v) => setPayForm({ ...payForm, next_due_date: v })}
                      className="bg-muted border-border"
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Required when a balance remains after this payment.
                    </p>
                  </div>
                )}
                <div>
                  <Label>Method</Label>
                  <Select
                    value={payForm.payment_method}
                    onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Remarks</Label>
                  <Input
                    value={payForm.remarks}
                    onChange={(e) => setPayForm({ ...payForm, remarks: e.target.value })}
                    className="bg-muted border-border"
                  />
                </div>
              </div>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" disabled={collecting}>
                {collecting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {collecting ? 'Saving…' : 'Save payment'}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-foreground flex items-center gap-2">
            <Receipt className="w-5 h-5" /> Payment history
          </CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">No payments yet.</p>
          ) : (
            <div className="space-y-2">
              {payments.map((batch) => {
                const isExpanded = expandedPayment === batch.id;
                const lines = batch.lines || [];
                return (
                  <div key={batch.id} className="border border-border rounded-lg overflow-hidden">
                    <div className="flex flex-wrap items-center gap-3 p-3 bg-muted/20">
                      <div className="flex-1 min-w-[120px]">
                        <p className="text-sm font-medium text-foreground">{batch.payment_date}</p>
                        <p className="text-xs font-mono text-muted-foreground">{batch.receipt_no}</p>
                      </div>
                      <div className="text-sm capitalize text-muted-foreground">
                        {batch.payment_method?.replace('_', ' ')}
                      </div>
                      <div className="text-sm min-w-[100px]">
                        <p className="text-xs text-muted-foreground">Next due</p>
                        <p className="font-medium text-foreground">{formatDate(batch.next_due_date)}</p>
                      </div>
                      <div className="text-sm text-right">
                        <p className="font-medium text-foreground">{formatCurrency(batch.total_amount)}</p>
                        <p className="text-xs text-muted-foreground">Remaining {formatCurrency(batch.remaining_balance)}</p>
                      </div>
                      <div className="flex gap-1">
                        {lines.length > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setExpandedPayment(isExpanded ? null : batch.id)}
                          >
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        )}
                        {batch.primary_payment_id && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="border-border text-blue-500 hover:text-blue-400 shrink-0"
                            onClick={() => handleOpenReceipt(batch)}
                            disabled={openingReceiptId === batch.id}
                            title="Open fee receipt PDF"
                          >
                            {openingReceiptId === batch.id ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <Receipt className="w-4 h-4 mr-1" />
                            )}
                            Receipt
                          </Button>
                        )}
                      </div>
                    </div>
                    {isExpanded && lines.length > 0 && (
                      <div className="px-3 pb-3 border-t border-border">
                        <p className="text-xs text-muted-foreground uppercase pt-2 pb-1">Student breakup</p>
                        <ul className="space-y-1">
                          {lines.map((line) => (
                            <li key={line.id} className="flex justify-between text-sm">
                              <span className="text-foreground">
                                {line.first_name} {line.last_name}
                                <span className="text-muted-foreground text-xs ml-1">({line.admission_no})</span>
                              </span>
                              <span className="font-medium">{formatCurrency(line.total_amount ?? line.amount)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentAccountDetail;
